import axios from "axios";
import { config } from "../Helpers/env";

// ============================================================================
// MOCK DATA FLAG - Set to false when the backend endpoints are ready
// ============================================================================
const USE_MOCK = false;

const BASE = config.smsUrl;

// ============================================================================
// HELPER - wrap mock data to match axios response shape { data: ... }
// ============================================================================
const mock = (payload, delay = 300) =>
  new Promise((resolve) =>
    setTimeout(() => resolve({ data: payload }), delay)
  );

// ============================================================================
// MOCK DATA GENERATORS
// ============================================================================

// -- Dashboard ---------------------------------------------------------------
const mockDashboardStats = () => ({
  pending_referrals: 12,
  todays_surgeries: 8,
  in_theatre: 2,
  completed_today: 4,
  cancelled_today: 1,
  upcoming_24h: 3,
  avg_theatre_utilisation: 72,
});

// -- Referrals ---------------------------------------------------------------
const mockReferrals = () => [
  {
    id: "REF-2026-0001",
    patient_number: "PT-100234",
    patient_name: "Mukasa Robert",
    age: 45,
    gender: "Male",
    referring_department: "Internal Medicine",
    referring_doctor: "Dr. Ssekitoleko Paul",
    diagnosis: "Cholecystitis",
    procedure_requested: "Laparoscopic Cholecystectomy",
    urgency: "elective",
    status: "pending",
    created_at: "2026-05-20T09:30:00Z",
  },
  {
    id: "REF-2026-0002",
    patient_number: "PT-100891",
    patient_name: "Nakamya Grace",
    age: 32,
    gender: "Female",
    referring_department: "Obstetrics & Gynaecology",
    referring_doctor: "Dr. Namubiru Harriet",
    diagnosis: "Uterine fibroids",
    procedure_requested: "Myomectomy",
    urgency: "urgent",
    status: "accepted",
    created_at: "2026-05-22T14:15:00Z",
  },
  {
    id: "REF-2026-0003",
    patient_number: "PT-101102",
    patient_name: "Ouma Daniel",
    age: 58,
    gender: "Male",
    referring_department: "Accident & Emergency",
    referring_doctor: "Dr. Akello Janet",
    diagnosis: "Open fracture right tibia",
    procedure_requested: "ORIF Right Tibia",
    urgency: "emergency",
    status: "scheduled",
    created_at: "2026-05-25T22:10:00Z",
  },
  {
    id: "REF-2026-0004",
    patient_number: "PT-100456",
    patient_name: "Kibuuka Moses",
    age: 67,
    gender: "Male",
    referring_department: "Internal Medicine",
    referring_doctor: "Dr. Mugisha Ivan",
    diagnosis: "Right inguinal hernia",
    procedure_requested: "Herniorrhaphy",
    urgency: "elective",
    status: "cancelled",
    created_at: "2026-05-18T11:00:00Z",
  },
  {
    id: "REF-2026-0005",
    patient_number: "PT-101330",
    patient_name: "Auma Florence",
    age: 28,
    gender: "Female",
    referring_department: "General Surgery",
    referring_doctor: "Dr. Okello Francis",
    diagnosis: "Acute appendicitis",
    procedure_requested: "Appendicectomy",
    urgency: "emergency",
    status: "pending",
    created_at: "2026-05-26T06:45:00Z",
  },
];

// -- Surgeries ---------------------------------------------------------------
const mockSurgeries = () => [
  {
    id: "SURG-2026-0010",
    patient_number: "PT-100234",
    patient_name: "Mukasa Robert",
    procedure: "Laparoscopic Cholecystectomy",
    lead_surgeon: "Dr. Kizza Andrew",
    theatre: "Theatre A",
    scheduled_date: "2026-05-26",
    estimated_start_time: "08:00",
    duration_hr: 1,
    duration_mins: 30,
    status: "scheduled",
    urgency: "elective",
  },
  {
    id: "SURG-2026-0011",
    patient_number: "PT-100891",
    patient_name: "Nakamya Grace",
    procedure: "Myomectomy",
    lead_surgeon: "Dr. Namubiru Harriet",
    theatre: "Theatre B",
    scheduled_date: "2026-05-26",
    estimated_start_time: "09:30",
    duration_hr: 2,
    duration_mins: 0,
    status: "in_progress",
    urgency: "urgent",
  },
  {
    id: "SURG-2026-0012",
    patient_number: "PT-101102",
    patient_name: "Ouma Daniel",
    procedure: "ORIF Right Tibia",
    lead_surgeon: "Dr. Kato Samuel",
    theatre: "Theatre A",
    scheduled_date: "2026-05-26",
    estimated_start_time: "14:00",
    duration_hr: 3,
    duration_mins: 0,
    status: "pre_op",
    urgency: "emergency",
  },
  {
    id: "SURG-2026-0009",
    patient_number: "PT-100789",
    patient_name: "Nalubega Sarah",
    procedure: "Thyroidectomy",
    lead_surgeon: "Dr. Kizza Andrew",
    theatre: "Theatre B",
    scheduled_date: "2026-05-25",
    estimated_start_time: "10:00",
    duration_hr: 2,
    duration_mins: 30,
    status: "completed",
    urgency: "elective",
  },
  {
    id: "SURG-2026-0008",
    patient_number: "PT-101330",
    patient_name: "Auma Florence",
    procedure: "Appendicectomy",
    lead_surgeon: "Dr. Okello Francis",
    theatre: "Procedure Room 1",
    scheduled_date: "2026-05-26",
    estimated_start_time: "07:00",
    duration_hr: 1,
    duration_mins: 0,
    status: "in_pacu",
    urgency: "emergency",
  },
];

// -- Theatres ----------------------------------------------------------------
const mockTheatres = () => [
  {
    id: "TH-001",
    name: "Theatre A",
    location: "Main Block, 2nd Floor",
    type: "general",
    equipment: ["Laparoscopy tower", "C-Arm", "Electrosurgery unit"],
    status: "occupied",
  },
  {
    id: "TH-002",
    name: "Theatre B",
    location: "Main Block, 2nd Floor",
    type: "general",
    equipment: ["Electrosurgery unit", "Anaesthesia machine"],
    status: "available",
  },
  {
    id: "TH-003",
    name: "Procedure Room 1",
    location: "Outpatient Wing, Ground Floor",
    type: "minor",
    equipment: ["Minor surgery set", "Suction machine"],
    status: "available",
  },
];

// -- Doctors -----------------------------------------------------------------
const mockDoctors = () => [
  {
    id: "DOC-001",
    name: "Dr. Kizza Andrew",
    speciality: "General Surgery",
    type: "internal",
    privileges: ["laparoscopy", "open_surgery", "thyroid"],
    available: true,
  },
  {
    id: "DOC-002",
    name: "Dr. Ssemakula Peter",
    speciality: "Orthopaedic Surgery",
    type: "visiting_consultant",
    privileges: ["ortho_trauma", "arthroplasty"],
    available: true,
  },
  {
    id: "DOC-003",
    name: "Dr. Namubiru Harriet",
    speciality: "Obstetrics & Gynaecology",
    type: "locum",
    privileges: ["gynaecology", "obstetric_surgery"],
    available: false,
  },
  {
    id: "DOC-004",
    name: "Dr. Kato Samuel",
    speciality: "Orthopaedics",
    type: "referred_specialist",
    privileges: ["ortho_trauma", "spine"],
    available: true,
  },
];

// -- Generic success response ------------------------------------------------
const mockSuccess = (message = "Operation successful") => ({
  status: "success",
  message,
});

// ============================================================================
// RESPONSE HELPERS
// ============================================================================
const unwrapResponse = (promise) =>
  promise.then((response) => {
    const payload = response?.data?.data !== undefined ? response.data.data : response.data;
    return { ...response, data: payload };
  });

const post = (url, data) => unwrapResponse(axios.post(url, data));
const get = (url) => unwrapResponse(axios.get(url));

// ============================================================================
// SERVICE DEFINITION
// ============================================================================

export const surgeryService = {
  // ==========================================================================
  // DASHBOARD
  // ==========================================================================
  getDashboardStats() {
    if (USE_MOCK) return mock({ ...mockDashboardStats() });
    return post(`${BASE}/surgery/dashboard/stats`);
  },

  // ==========================================================================
  // REFERRALS
  // ==========================================================================
  createReferral(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Referral created"), referral_id: "REF-2026-0006" });
    return post(`${BASE}/surgery/referral/create`, data);
  },

  listReferrals(filters) {
    if (USE_MOCK) return mock({ referrals: mockReferrals(), total: 5 });
    return post(`${BASE}/surgery/referral/list`, filters);
  },

  viewReferral(id) {
    if (USE_MOCK) {
      const referral = mockReferrals().find((r) => r.id === id) || mockReferrals()[0];
      return mock(referral);
    }
    return post(`${BASE}/surgery/referral/view`, { referral_id: id });
  },

  getPatientSurgeryHistory(patientNumber) {
    if (USE_MOCK) {
      return mock({
        patient_number: patientNumber,
        surgeries: mockSurgeries().filter((s) => s.patient_number === patientNumber),
      });
    }
    return post(`${BASE}/surgery/referral/patient_history`, { patient_number: patientNumber });
  },

  getActiveReferrals(patientNumber) {
    if (USE_MOCK) {
      return mock({
        referrals: mockReferrals().filter(
          (r) => r.patient_number === patientNumber && !["cancelled", "completed"].includes(r.status)
        ),
      });
    }
    return post(`${BASE}/surgery/referral/active`, { patient_number: patientNumber });
  },

  scheduleFromReferral(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Surgery scheduled from referral"), surgery_id: "SURG-2026-0013" });
    return post(`${BASE}/surgery/referral/schedule`, data);
  },

  cancelReferral(data) {
    if (USE_MOCK) return mock(mockSuccess("Referral cancelled"));
    return post(`${BASE}/surgery/referral/cancel`, data);
  },

  getReferralStats(data) {
    if (USE_MOCK) {
      return mock({
        total: 48,
        pending: 12,
        accepted: 18,
        scheduled: 10,
        cancelled: 5,
        completed: 3,
      });
    }
    return post(`${BASE}/surgery/referral/stats`, data);
  },

  // ==========================================================================
  // SURGERIES (CRUD + transitions)
  // ==========================================================================
  createSurgery(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Surgery created"), surgery_id: "SURG-2026-0014" });
    return post(`${BASE}/surgery/create_surgery_visit`, data);
  },

  listSurgeries(page = 1, filters) {
    if (USE_MOCK) return mock({ surgeries: mockSurgeries(), total: 5, page, per_page: 10 });
    return post(`${BASE}/surgery/list/${page}`, filters);
  },

  viewSurgery(data) {
    if (USE_MOCK) {
      const surgery = mockSurgeries()[0];
      return mock({
        ...surgery,
        pre_op: { assessment_done: true, consent_signed: true, checklist_complete: true },
        who_checklist: { sign_in: true, time_out: false, sign_out: false },
      });
    }
    return post(`${BASE}/surgery/view_surgery_full_details`, data);
  },

  updateSurgery(data) {
    if (USE_MOCK) return mock(mockSuccess("Surgery details updated"));
    return post(`${BASE}/surgery/update_surgery_details`, data);
  },

  startSurgery(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Surgery started"), actual_start_time: new Date().toISOString() });
    return post(`${BASE}/surgery/start_surgery`, data);
  },

  endSurgery(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Surgery ended"), actual_end_time: new Date().toISOString() });
    return post(`${BASE}/surgery/end_surgery`, data);
  },

  rescheduleSurgery(data) {
    if (USE_MOCK) return mock(mockSuccess("Surgery rescheduled"));
    return post(`${BASE}/surgery/reschedule_surgery`, data);
  },

  cancelSurgery(data) {
    if (USE_MOCK) return mock(mockSuccess("Surgery cancelled"));
    return post(`${BASE}/surgery/cancel_surgery`, data);
  },

  // ==========================================================================
  // ADMISSION
  // ==========================================================================
  createSurgeryAdmission(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Admission created"), admission_id: "ADM-2026-0045" });
    return post(`${BASE}/visit/admission/create`, data);
  },

  linkAdmission(data) {
    if (USE_MOCK) return mock(mockSuccess("Admission linked to surgery"));
    return post(`${BASE}/surgery/link_admission`, data);
  },

  getAdmissionStatus(surgeryId) {
    if (USE_MOCK) {
      return mock({
        surgery_id: surgeryId,
        admitted: true,
        admission_id: "ADM-2026-0045",
        ward: "Surgical Ward A",
        bed: "SW-A-12",
        admitted_at: "2026-05-25T18:00:00Z",
      });
    }
    return post(`${BASE}/surgery/admission_status`, { surgery_id: surgeryId });
  },

  getAvailableBeds(data) {
    if (USE_MOCK) {
      return mock({
        beds: [
          { id: "SW-A-05", ward: "Surgical Ward A", type: "general", available: true },
          { id: "SW-A-12", ward: "Surgical Ward A", type: "general", available: true },
          { id: "SW-B-03", ward: "Surgical Ward B", type: "private", available: true },
          { id: "ICU-02", ward: "ICU", type: "icu", available: true },
        ],
      });
    }
    return post(`${BASE}/bed/available`, data);
  },

  // ==========================================================================
  // OUTPATIENT DAY-CASE
  // ==========================================================================
  dayCaseCheckIn(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Day-case patient checked in"), checkin_time: new Date().toISOString() });
    return post(`${BASE}/surgery/daycase/checkin`, data);
  },

  saveDayCaseObs(data) {
    if (USE_MOCK) return mock(mockSuccess("Day-case observations saved"));
    return post(`${BASE}/surgery/daycase/save`, data);
  },

  dischargeDayCase(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Day-case patient discharged"), discharge_time: new Date().toISOString() });
    return post(`${BASE}/surgery/daycase/discharge`, data);
  },

  convertToInpatient(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Converted to inpatient"), admission_id: "ADM-2026-0046" });
    return post(`${BASE}/surgery/convert_to_inpatient`, data);
  },

  // ==========================================================================
  // THEATRE
  // ==========================================================================
  listTheatres() {
    if (USE_MOCK) return mock({ theatres: mockTheatres() });
    return post(`${BASE}/theatre/list`);
  },

  theatreCalendar(data) {
    if (USE_MOCK) {
      return mock({
        date: data?.date || "2026-05-26",
        slots: [
          { theatre: "Theatre A", time: "08:00-09:30", surgery_id: "SURG-2026-0010", procedure: "Lap. Cholecystectomy", status: "booked" },
          { theatre: "Theatre A", time: "10:00-11:00", surgery_id: null, procedure: null, status: "available" },
          { theatre: "Theatre A", time: "14:00-17:00", surgery_id: "SURG-2026-0012", procedure: "ORIF Right Tibia", status: "booked" },
          { theatre: "Theatre B", time: "09:30-11:30", surgery_id: "SURG-2026-0011", procedure: "Myomectomy", status: "in_progress" },
          { theatre: "Theatre B", time: "13:00-15:30", surgery_id: null, procedure: null, status: "available" },
          { theatre: "Procedure Room 1", time: "07:00-08:00", surgery_id: "SURG-2026-0008", procedure: "Appendicectomy", status: "completed" },
        ],
      });
    }
    return post(`${BASE}/theatre/calendar`, data);
  },

  bookTheatre(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Theatre booked"), booking_id: "BK-2026-0022" });
    return post(`${BASE}/theatre/book`, data);
  },

  checkAvailability(data) {
    if (USE_MOCK) {
      return mock({
        available: true,
        theatre: data?.theatre_id || "TH-001",
        date: data?.date || "2026-05-27",
        slots: ["08:00-10:00", "13:00-15:00", "16:00-18:00"],
      });
    }
    return post(`${BASE}/theatre/check_availability`, data);
  },

  // ==========================================================================
  // DOCTOR REGISTRY
  // ==========================================================================
  searchDoctors(data) {
    if (USE_MOCK) {
      let results = mockDoctors();
      if (data?.query) {
        const q = data.query.toLowerCase();
        results = results.filter((d) => d.name.toLowerCase().includes(q) || d.speciality.toLowerCase().includes(q));
      }
      return mock({ doctors: results });
    }
    return post(`${BASE}/doctors/search`, data);
  },

  registerDoctor(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Doctor registered"), doctor_id: "DOC-005" });
    return post(`${BASE}/doctors/register`, data);
  },

  checkPrivileges(data) {
    if (USE_MOCK) {
      return mock({
        doctor_id: data?.doctor_id || "DOC-001",
        has_privilege: true,
        privileges: ["laparoscopy", "open_surgery", "thyroid"],
      });
    }
    return post(`${BASE}/doctors/privileges/check`, data);
  },

  // ==========================================================================
  // PRE-OP
  // ==========================================================================
  createPreOpAssessment(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Pre-op assessment created"), assessment_id: "POA-2026-0015" });
    return post(`${BASE}/surgery/preop_assessment`, data);
  },

  viewPreOpAssessment(surgeryId) {
    if (USE_MOCK) {
      return mock({
        surgery_id: surgeryId,
        assessment_id: "POA-2026-0015",
        asa_class: "II",
        allergies: "Penicillin",
        comorbidities: ["Hypertension", "Type 2 DM"],
        airway: "Mallampati II",
        fasting_status: "NPO 8 hours",
        blood_group: "O+",
        hb_level: 13.2,
        assessed_by: "Dr. Lubega Timothy",
        assessed_at: "2026-05-25T16:30:00Z",
      });
    }
    return post(`${BASE}/surgery/preop/view`, { surgery_id: surgeryId });
  },

  createConsent(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Consent recorded"), consent_id: "CON-2026-0020" });
    return post(`${BASE}/surgery/consent`, data);
  },

  listConsents(surgeryId) {
    if (USE_MOCK) {
      return mock({
        consents: [
          {
            id: "CON-2026-0020",
            surgery_id: surgeryId,
            type: "surgical",
            signed_by_patient: true,
            witnessed_by: "Nurse Babirye Joyce",
            signed_at: "2026-05-25T17:00:00Z",
          },
          {
            id: "CON-2026-0021",
            surgery_id: surgeryId,
            type: "anaesthesia",
            signed_by_patient: true,
            witnessed_by: "Nurse Babirye Joyce",
            signed_at: "2026-05-25T17:05:00Z",
          },
        ],
      });
    }
    return post(`${BASE}/surgery/consent/list`, { surgery_id: surgeryId });
  },

  savePreOpChecklist(data) {
    if (USE_MOCK) return mock(mockSuccess("Pre-op checklist saved"));
    return post(`${BASE}/surgery/preop_checklist`, data);
  },

  getPreOpReadiness(surgeryId) {
    if (USE_MOCK) return mock({ assessment: false, checklist: false, consent: false });
    return post(`${BASE}/surgery/preop/readiness`, { surgery_id: surgeryId });
  },

  // ==========================================================================
  // WHO CHECKLIST
  // ==========================================================================
  saveWHOPhase(data) {
    if (USE_MOCK) return mock(mockSuccess(`WHO checklist phase '${data?.phase || "sign_in"}' saved`));
    return post(`${BASE}/surgery/who_checklist`, data);
  },

  viewWHOChecklist(surgeryId) {
    if (USE_MOCK) {
      return mock({
        surgery_id: surgeryId,
        sign_in: {
          completed: true,
          completed_by: "Dr. Lubega Timothy",
          completed_at: "2026-05-26T07:55:00Z",
          patient_identity_confirmed: true,
          site_marked: true,
          anaesthesia_check: true,
          allergies_checked: true,
        },
        time_out: {
          completed: false,
          team_introduced: false,
          procedure_confirmed: false,
          antibiotics_given: false,
        },
        sign_out: {
          completed: false,
          instrument_count_correct: false,
          specimen_labelled: false,
          concerns_addressed: false,
        },
      });
    }
    return post(`${BASE}/surgery/who_checklist/view`, { surgery_id: surgeryId });
  },

  // ==========================================================================
  // INTRA-OP
  // ==========================================================================
  createAnaesthesiaRecord(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Anaesthesia record created"), record_id: "AN-2026-0030" });
    return post(`${BASE}/surgery/anaesthesia_record`, data);
  },

  getAnaesthesiaRecord(surgeryId) {
    if (USE_MOCK) return mock(null);
    return post(`${BASE}/surgery/anaesthesia_record/view`, { surgery_id: surgeryId });
  },

  addVitals(data) {
    if (USE_MOCK) return mock(mockSuccess("Vitals recorded"));
    return post(`${BASE}/surgery/intraop_vitals`, data);
  },

  addDrug(data) {
    if (USE_MOCK) return mock(mockSuccess("Drug administered and recorded"));
    return post(`${BASE}/surgery/drug_administration`, data);
  },

  saveOperativeNotes(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Operative notes saved"), notes_id: "ON-2026-0018" });
    return post(`${BASE}/surgery/operative_note`, data);
  },

  getOperativeNote(surgeryId) {
    if (USE_MOCK) return mock(null);
    return post(`${BASE}/surgery/operative_note/view`, { surgery_id: surgeryId });
  },

  addConsumable(data) {
    if (USE_MOCK) return mock(mockSuccess("Consumable added"));
    return post(`${BASE}/surgery/consumables/add`, data);
  },

  listConsumables(surgeryId) {
    if (USE_MOCK) {
      return mock({
        consumables: [
          { id: 1, name: "Surgical gloves (size 7.5)", quantity: 4, unit_cost: 5000, total: 20000 },
          { id: 2, name: "Vicryl 2-0 suture", quantity: 2, unit_cost: 35000, total: 70000 },
          { id: 3, name: "Gauze swabs (pack of 10)", quantity: 3, unit_cost: 8000, total: 24000 },
          { id: 4, name: "Foley catheter 16Fr", quantity: 1, unit_cost: 15000, total: 15000 },
        ],
      });
    }
    return post(`${BASE}/surgery/consumables/list`, { surgery_id: surgeryId });
  },

  deleteConsumable(data) {
    if (USE_MOCK) return mock(mockSuccess("Consumable removed and stock restored"));
    return post(`${BASE}/surgery/consumables/remove`, data);
  },

  searchSurgeryProducts(productName) {
    return axios.post(`${BASE}/cinventory/product_search_by_name`,
      new URLSearchParams({ product_name: productName, to_store: 'Surgery' })
    );
  },

  getProductBatches(productId) {
    return axios.post(`${BASE}/cinventory/retrieve_product_batch_ids`,
      new URLSearchParams({ product_id: productId, location: 4 })
    );
  },

  // ==========================================================================
  // POST-OP
  // ==========================================================================
  admitToPACU(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Patient admitted to PACU"), pacu_admission_time: new Date().toISOString() });
    return post(`${BASE}/surgery/pacu/admit`, data);
  },

  addPACUVitals(data) {
    if (USE_MOCK) return mock(mockSuccess("PACU vitals recorded"));
    return post(`${BASE}/surgery/pacu/vitals`, data);
  },

  getPACUVitals(data) {
    if (USE_MOCK) return mock({ entries: [] });
    return post(`${BASE}/surgery/pacu/vitals/view`, data);
  },

  dischargePACU(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Patient discharged from PACU"), aldrete_score: 9 });
    return post(`${BASE}/surgery/pacu/discharge`, data);
  },

  createPostOpOrder(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Post-op order created"), order_id: "PO-2026-0055" });
    return post(`${BASE}/surgery/postop_orders/create`, data);
  },

  reportComplication(data) {
    if (USE_MOCK) return mock({ ...mockSuccess("Complication reported"), complication_id: "COMP-2026-0008" });
    return post(`${BASE}/surgery/postop_complication`, data);
  },

  listComplications(surgeryId) {
    if (USE_MOCK) return mock([]);
    return post(`${BASE}/surgery/postop_complications/list`, { surgery_id: surgeryId });
  },

  // ==========================================================================
  // BILLING
  // ==========================================================================
  generateEstimate(data) {
    if (USE_MOCK) {
      return mock({
        estimate_id: "EST-2026-0033",
        surgery_id: data?.surgery_id || "SURG-2026-0010",
        items: [
          { description: "Theatre fee", amount: 500000 },
          { description: "Surgeon fee", amount: 800000 },
          { description: "Anaesthesia fee", amount: 400000 },
          { description: "Consumables (estimated)", amount: 250000 },
          { description: "Ward stay (2 nights)", amount: 300000 },
        ],
        total_estimate: 2250000,
        currency: "UGX",
      });
    }
    return post(`${BASE}/surgery/billing/estimate`, data);
  },

  submitPreAuth(data) {
    if (USE_MOCK) {
      return mock({
        ...mockSuccess("Pre-authorisation request submitted"),
        preauth_id: "PA-2026-0012",
        status: "pending",
      });
    }
    return post(`${BASE}/surgery/billing/preauth/submit`, data);
  },

  updatePreAuth(data) {
    return post(`${BASE}/surgery/billing/preauth/update`, data);
  },

  updateBillingStatus(data) {
    return post(`${BASE}/surgery/billing/status/update`, data);
  },

  getBillSummary(surgeryId) {
    if (USE_MOCK) {
      return mock({
        surgery_id: surgeryId,
        status: "partially_paid",
        items: [
          { description: "Theatre fee", amount: 500000, paid: 500000 },
          { description: "Surgeon fee", amount: 800000, paid: 0 },
          { description: "Anaesthesia fee", amount: 400000, paid: 400000 },
          { description: "Consumables", amount: 129000, paid: 0 },
        ],
        total: 1829000,
        paid: 900000,
        balance: 929000,
        currency: "UGX",
      });
    }
    return post(`${BASE}/surgery/billing/summary`, { surgery_id: surgeryId });
  },

  getAuditLog(surgeryId) {
    if (USE_MOCK) return mock([]);
    return get(`${BASE}/surgery/audit_log/${surgeryId}`);
  },

  searchStaff(query = '') {
    return post(`${BASE}/surgery/search_staff`, { query });
  },
};
